import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../services/api';

// Glassmorphism style
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

interface Pipeline {
  id: number;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  owner: number;
  owner_name: string;
  display_order: number;
  color: string;
  total_deals: number;
  total_value: number;
  created_at: string;
  updated_at: string;
  stages?: Stage[];
}

interface Stage {
  id: number;
  name: string;
  description: string;
  pipeline: number;
  pipeline_name: string;
  stage_type: string;
  stage_type_display: string;
  display_order: number;
  color: string;
  probability: number | null;
  is_won_stage: boolean;
  is_lost_stage: boolean;
  expected_days: number | null;
  deal_count: number;
  total_value: number;
  created_at: string;
  updated_at: string;
}

const PipelineManagement = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);

  const [pipelineForm, setPipelineForm] = useState({
    name: '',
    description: '',
    color: '#6366f1',
  });

  const [stageForm, setStageForm] = useState({
    name: '',
    description: '',
    color: '#94a3b8',
    display_order: 0,
    probability: 50,
    is_won_stage: false,
    is_lost_stage: false,
    expected_days: null as number | null,
    stage_type: 'progress',
  });

  useEffect(() => {
    loadPipelines();
  }, []);

  const loadPipelines = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pipelines/pipelines/');
      const pipelinesArray = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPipelines(pipelinesArray);
    } catch (err: any) {
      console.error('Erro ao carregar pipelines:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPipelineDetails = async (pipelineId: number) => {
    try {
      const response = await api.get(`/pipelines/pipelines/${pipelineId}/`);
      setSelectedPipeline(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar detalhes do pipeline:', err);
    }
  };

  const handleCreatePipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/pipelines/pipelines/', pipelineForm);
      setShowPipelineModal(false);
      setPipelineForm({ name: '', description: '', color: '#6366f1' });
      loadPipelines();
    } catch (err: any) {
      console.error('Erro ao criar pipeline:', err);
    }
  };

  const handleUpdatePipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPipeline) return;

    try {
      await api.patch(`/pipelines/pipelines/${editingPipeline.id}/`, pipelineForm);
      setShowPipelineModal(false);
      setEditingPipeline(null);
      setPipelineForm({ name: '', description: '', color: '#6366f1' });
      loadPipelines();
    } catch (err: any) {
      console.error('Erro ao atualizar pipeline:', err);
    }
  };

  const handleDeletePipeline = async (pipelineId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este pipeline?')) return;

    try {
      await api.delete(`/pipelines/pipelines/${pipelineId}/`);
      loadPipelines();
      if (selectedPipeline?.id === pipelineId) {
        setSelectedPipeline(null);
      }
    } catch (err: any) {
      console.error('Erro ao deletar pipeline:', err);
    }
  };

  const handleClonePipeline = async (pipelineId: number) => {
    try {
      await api.post(`/pipelines/pipelines/${pipelineId}/clone/`);
      loadPipelines();
    } catch (err: any) {
      console.error('Erro ao clonar pipeline:', err);
    }
  };

  const handleCreateStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPipeline) return;

    try {
      await api.post('/pipelines/stages/', {
        ...stageForm,
        pipeline: selectedPipeline.id,
      });
      setShowStageModal(false);
      resetStageForm();
      loadPipelineDetails(selectedPipeline.id);
    } catch (err: any) {
      console.error('Erro ao criar stage:', err);
    }
  };

  const handleUpdateStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStage) return;

    try {
      await api.patch(`/pipelines/stages/${editingStage.id}/`, stageForm);
      setShowStageModal(false);
      setEditingStage(null);
      resetStageForm();
      if (selectedPipeline) loadPipelineDetails(selectedPipeline.id);
    } catch (err: any) {
      console.error('Erro ao atualizar stage:', err);
    }
  };

  const handleDeleteStage = async (stageId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este stage?')) return;

    try {
      await api.delete(`/pipelines/stages/${stageId}/`);
      if (selectedPipeline) loadPipelineDetails(selectedPipeline.id);
    } catch (err: any) {
      console.error('Erro ao deletar stage:', err);
    }
  };

  const handleReorderStage = async (stageId: number, direction: 'up' | 'down') => {
    if (!selectedPipeline?.stages) return;

    // Separate regular stages from won/lost stages
    const regularStages = selectedPipeline.stages
      .filter(s => !s.is_won_stage && !s.is_lost_stage)
      .sort((a, b) => a.display_order - b.display_order);

    const currentIndex = regularStages.findIndex(s => s.id === stageId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= regularStages.length) return;

    // Calculate new order (swap with neighbor)
    const newOrder = regularStages[newIndex].display_order;

    try {
      await api.post(`/pipelines/stages/${stageId}/reorder/`, { display_order: newOrder });
      loadPipelineDetails(selectedPipeline.id);
    } catch (err: any) {
      console.error('Erro ao reordenar stage:', err);
    }
  };

  const openEditPipeline = (pipeline: Pipeline) => {
    setEditingPipeline(pipeline);
    setPipelineForm({
      name: pipeline.name,
      description: pipeline.description || '',
      color: pipeline.color,
    });
    setShowPipelineModal(true);
  };

  const openEditStage = (stage: Stage) => {
    setEditingStage(stage);
    setStageForm({
      name: stage.name,
      description: stage.description || '',
      color: stage.color || '#94a3b8',
      display_order: stage.display_order,
      probability: stage.probability || 50,
      is_won_stage: stage.is_won_stage,
      is_lost_stage: stage.is_lost_stage,
      expected_days: stage.expected_days,
      stage_type: stage.stage_type,
    });
    setShowStageModal(true);
  };

  const resetStageForm = () => {
    setStageForm({
      name: '',
      description: '',
      color: '#94a3b8',
      display_order: selectedPipeline?.stages?.length || 0,
      probability: 50,
      is_won_stage: false,
      is_lost_stage: false,
      expected_days: null,
      stage_type: 'progress',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      overflow: 'hidden',
      p: 2,
      gap: 2
    }}>
      {/* Header */}
      <Box sx={{
        ...glassStyle,
        borderRadius: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/pipelines')}
            sx={{
              background: 'rgba(0,0,0,0.05)',
              '&:hover': { background: 'rgba(0,0,0,0.1)' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SettingsIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Gestão de Pipelines
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Crie e gerencie seus funis de vendas
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingPipeline(null);
            setPipelineForm({ name: '', description: '', color: '#6366f1' });
            setShowPipelineModal(true);
          }}
          sx={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: '0 4px 15px rgba(99,102,241,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            }
          }}
        >
          Novo Pipeline
        </Button>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
        {/* Pipelines List */}
        <Box sx={{
          width: 380,
          minWidth: 380,
          ...glassStyle,
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Pipelines ({pipelines.length})
            </Typography>
          </Box>

          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: 1.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 3 }
          }}>
            {pipelines.map(pipeline => (
              <Box
                key={pipeline.id}
                onClick={() => loadPipelineDetails(pipeline.id)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: selectedPipeline?.id === pipeline.id
                    ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))'
                    : 'rgba(255,255,255,0.8)',
                  border: selectedPipeline?.id === pipeline.id
                    ? '2px solid rgba(99,102,241,0.4)'
                    : '1px solid rgba(0,0,0,0.05)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.08)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: pipeline.color
                  }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>
                    {pipeline.name}
                  </Typography>
                  {pipeline.is_default && (
                    <Chip
                      label="Padrão"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        background: 'rgba(99,102,241,0.1)',
                        color: '#6366f1'
                      }}
                    />
                  )}
                </Box>

                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 12, mb: 1.5 }}>
                  {pipeline.description || 'Sem descrição'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      {pipeline.total_deals} negócios
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      {formatCurrency(Number(pipeline.total_value))}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditPipeline(pipeline);
                      }}
                      sx={{
                        background: 'rgba(0,0,0,0.05)',
                        '&:hover': { background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clonar">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClonePipeline(pipeline.id);
                      }}
                      sx={{
                        background: 'rgba(0,0,0,0.05)',
                        '&:hover': { background: 'rgba(99,102,241,0.1)', color: '#6366f1' }
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Deletar">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePipeline(pipeline.id);
                      }}
                      sx={{
                        background: 'rgba(0,0,0,0.05)',
                        '&:hover': { background: 'rgba(239,68,68,0.1)', color: '#ef4444' }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Stages Panel */}
        <Box sx={{
          flex: 1,
          ...glassStyle,
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          {selectedPipeline ? (
            <>
              {/* Pipeline Header */}
              <Box sx={{
                p: 2,
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: selectedPipeline.color
                    }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {selectedPipeline.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    {selectedPipeline.description || 'Sem descrição'}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingStage(null);
                    resetStageForm();
                    setShowStageModal(true);
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #16a34a, #15803d)'
                    }
                  }}
                >
                  Novo Stage
                </Button>
              </Box>

              {/* Stages List */}
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 3 }
              }}>
                {/* Regular Stages (reorderable) */}
                {(() => {
                  const regularStages = selectedPipeline.stages
                    ?.filter(s => !s.is_won_stage && !s.is_lost_stage)
                    .sort((a, b) => a.display_order - b.display_order) || [];

                  const wonLostStages = selectedPipeline.stages
                    ?.filter(s => s.is_won_stage || s.is_lost_stage)
                    .sort((a, b) => (a.is_won_stage ? 0 : 1) - (b.is_won_stage ? 0 : 1)) || [];

                  return (
                    <>
                      {/* Regular stages header */}
                      {regularStages.length > 0 && (
                        <Typography variant="caption" sx={{
                          display: 'block',
                          mb: 1.5,
                          color: '#6b7280',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}>
                          Etapas do Funil ({regularStages.length})
                        </Typography>
                      )}

                      {regularStages.map((stage, index) => (
                        <Box
                          key={stage.id}
                          sx={{
                            p: 2.5,
                            mb: 2,
                            borderRadius: 2,
                            background: 'rgba(255,255,255,0.9)',
                            backdropFilter: 'blur(10px)',
                            border: `2px solid ${stage.color}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: '0 8px 25px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {/* Order number */}
                              <Box sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: stage.color,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: 13
                              }}>
                                {index + 1}
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                  {stage.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 13 }}>
                                  {stage.description || 'Sem descrição'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditStage(stage)}
                                  sx={{
                                    background: 'rgba(0,0,0,0.05)',
                                    '&:hover': { background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Deletar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteStage(stage.id)}
                                  sx={{
                                    background: 'rgba(0,0,0,0.05)',
                                    '&:hover': { background: 'rgba(239,68,68,0.1)', color: '#ef4444' }
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pt: 2,
                            borderTop: '1px solid rgba(0,0,0,0.05)'
                          }}>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingUpIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  {stage.deal_count} negócios
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachMoneyIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                  {formatCurrency(Number(stage.total_value))}
                                </Typography>
                              </Box>
                              {stage.probability !== null && (
                                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: 12 }}>
                                  {stage.probability}% probabilidade
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Mover para cima">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={index === 0}
                                    onClick={() => handleReorderStage(stage.id, 'up')}
                                    sx={{
                                      background: index === 0 ? 'transparent' : 'rgba(99,102,241,0.1)',
                                      color: index === 0 ? '#d1d5db' : '#6366f1',
                                      '&:hover': { background: 'rgba(99,102,241,0.2)' }
                                    }}
                                  >
                                    <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Mover para baixo">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={index === regularStages.length - 1}
                                    onClick={() => handleReorderStage(stage.id, 'down')}
                                    sx={{
                                      background: index === regularStages.length - 1 ? 'transparent' : 'rgba(99,102,241,0.1)',
                                      color: index === regularStages.length - 1 ? '#d1d5db' : '#6366f1',
                                      '&:hover': { background: 'rgba(99,102,241,0.2)' }
                                    }}
                                  >
                                    <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      ))}

                      {/* Divider for Won/Lost stages */}
                      {wonLostStages.length > 0 && (
                        <>
                          <Box sx={{
                            my: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                          }}>
                            <Box sx={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.1)' }} />
                            <Typography variant="caption" sx={{
                              color: '#9ca3af',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}>
                              Etapas Finais (Fixas)
                            </Typography>
                            <Box sx={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.1)' }} />
                          </Box>

                          {wonLostStages.map((stage) => (
                            <Box
                              key={stage.id}
                              sx={{
                                p: 2.5,
                                mb: 2,
                                borderRadius: 2,
                                background: stage.is_won_stage
                                  ? 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.03))'
                                  : 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(239,68,68,0.03))',
                                backdropFilter: 'blur(10px)',
                                border: `2px solid ${stage.is_won_stage ? '#22c55e' : '#ef4444'}`,
                                opacity: 0.9
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {stage.is_won_stage ? (
                                    <CheckCircleIcon sx={{ fontSize: 24, color: '#22c55e' }} />
                                  ) : (
                                    <CancelIcon sx={{ fontSize: 24, color: '#ef4444' }} />
                                  )}
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                        {stage.name}
                                      </Typography>
                                      <Chip
                                        label={stage.is_won_stage ? 'Ganho' : 'Perda'}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: 10,
                                          background: stage.is_won_stage ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                          color: stage.is_won_stage ? '#22c55e' : '#ef4444',
                                          fontWeight: 700
                                        }}
                                      />
                                      <Chip
                                        label="Posição Fixa"
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: 10,
                                          background: 'rgba(0,0,0,0.05)',
                                          color: '#9ca3af'
                                        }}
                                      />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 13 }}>
                                      {stage.description || 'Sem descrição'}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Editar">
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditStage(stage)}
                                      sx={{
                                        background: 'rgba(0,0,0,0.05)',
                                        '&:hover': { background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 3, mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TrendingUpIcon sx={{ fontSize: 16, color: stage.is_won_stage ? '#22c55e' : '#ef4444' }} />
                                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                    {stage.deal_count} negócios
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AttachMoneyIcon sx={{ fontSize: 16, color: stage.is_won_stage ? '#22c55e' : '#ef4444' }} />
                                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                    {formatCurrency(Number(stage.total_value))}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                    </>
                  );
                })()}

                {(!selectedPipeline.stages || selectedPipeline.stages.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h1" sx={{ fontSize: 48, mb: 2 }}>📋</Typography>
                    <Typography variant="body1" sx={{ color: '#6b7280' }}>
                      Nenhum stage configurado
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      Adicione stages para organizar seu funil de vendas
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: 4
            }}>
              <Typography variant="h1" sx={{ fontSize: 64, mb: 2 }}>📊</Typography>
              <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                Selecione um pipeline
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Escolha um pipeline da lista para visualizar seus stages
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Pipeline Modal */}
      <Dialog
        open={showPipelineModal}
        onClose={() => {
          setShowPipelineModal(false);
          setEditingPipeline(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, ...glassStyle } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          {editingPipeline ? 'Editar Pipeline' : 'Novo Pipeline'}
        </DialogTitle>
        <form onSubmit={editingPipeline ? handleUpdatePipeline : handleCreatePipeline}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="Nome"
              value={pipelineForm.name}
              onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={3}
              value={pipelineForm.description}
              onChange={(e) => setPipelineForm({ ...pipelineForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Cor</Typography>
              <input
                type="color"
                value={pipelineForm.color}
                onChange={(e) => setPipelineForm({ ...pipelineForm, color: e.target.value })}
                style={{
                  width: '100%',
                  height: 48,
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            <Button onClick={() => {
              setShowPipelineModal(false);
              setEditingPipeline(null);
            }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: editingPipeline
                  ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                  : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                '&:hover': {
                  background: editingPipeline
                    ? 'linear-gradient(135deg, #d97706, #b45309)'
                    : 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                }
              }}
            >
              {editingPipeline ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Stage Modal */}
      <Dialog
        open={showStageModal}
        onClose={() => {
          setShowStageModal(false);
          setEditingStage(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, ...glassStyle } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', fontWeight: 700 }}>
          {editingStage ? 'Editar Stage' : 'Novo Stage'}
        </DialogTitle>
        <form onSubmit={editingStage ? handleUpdateStage : handleCreateStage}>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="Nome"
              value={stageForm.name}
              onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={2}
              value={stageForm.description}
              onChange={(e) => setStageForm({ ...stageForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={stageForm.stage_type}
                onChange={(e) => setStageForm({ ...stageForm, stage_type: e.target.value })}
                label="Tipo"
              >
                <MenuItem value="entry">Entrada</MenuItem>
                <MenuItem value="progress">Em Progresso</MenuItem>
                <MenuItem value="review">Revisão</MenuItem>
                <MenuItem value="won">Ganho</MenuItem>
                <MenuItem value="lost">Perdido</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Cor</Typography>
              <input
                type="color"
                value={stageForm.color}
                onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })}
                style={{
                  width: '100%',
                  height: 48,
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="Probabilidade (%)"
                type="number"
                value={stageForm.probability || 0}
                onChange={(e) => setStageForm({ ...stageForm, probability: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 100 }}
              />
              <TextField
                label="Dias Esperados"
                type="number"
                value={stageForm.expected_days || ''}
                onChange={(e) => setStageForm({ ...stageForm, expected_days: parseInt(e.target.value) || null })}
                inputProps={{ min: 0 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stageForm.is_won_stage}
                    onChange={(e) => setStageForm({ ...stageForm, is_won_stage: e.target.checked })}
                    sx={{ '&.Mui-checked': { color: '#22c55e' } }}
                  />
                }
                label="Stage de Ganho"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stageForm.is_lost_stage}
                    onChange={(e) => setStageForm({ ...stageForm, is_lost_stage: e.target.checked })}
                    sx={{ '&.Mui-checked': { color: '#ef4444' } }}
                  />
                }
                label="Stage de Perda"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            <Button onClick={() => {
              setShowStageModal(false);
              setEditingStage(null);
            }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: editingStage
                  ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                  : 'linear-gradient(135deg, #22c55e, #16a34a)',
                '&:hover': {
                  background: editingStage
                    ? 'linear-gradient(135deg, #d97706, #b45309)'
                    : 'linear-gradient(135deg, #16a34a, #15803d)'
                }
              }}
            >
              {editingStage ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default PipelineManagement;
